import {
  KoButton,
  KoForm,
  KoModal,
  KoSelect,
  KoInput,
  KoText,
  KoTitle,
  KoToast,
  KoTag,
  KoTabs,
  KoEmpty,
  KoFormBuilder
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import ReleaseSubNav from './ReleaseSubNav';
import styles from './releases.module.scss';

const SECTION_CONFIG = {
  features: { label: 'New Features', icon: 'StarOutlined', color: 'var(--palette-info-main)' },
  improvements: { label: 'Improvements', icon: 'ThunderboltOutlined', color: 'var(--palette-secondary-main)' },
  bugfixes: { label: 'Bug Fixes', icon: 'BugOutlined', color: 'var(--palette-error-main)' },
  security: { label: 'Security', icon: 'LockOutlined', color: 'var(--palette-warning-main)' }
};

const MOCK_NOTES = {
  name: 'Platform Upgrade',
  version: 'v4.2.0',
  date: '2026-03-15',
  prCount: 12,
  ticketCount: 8,
  sections: {
    features: [
      { ticketId: 'KWP-882', description: 'Real-time dashboard analytics with revenue chart, user activity heatmap, and conversion funnel', author: 'Alice Park' },
      { ticketId: 'KWP-912', description: 'Granular notification preferences panel with per-category controls for email, push, and in-app', author: 'Alice Park' },
      { ticketId: 'KWP-920', description: 'Team activity feed with real-time collaboration indicators', author: 'Sarah Kim' }
    ],
    improvements: [
      { ticketId: 'KWP-905', description: 'Migrated user service to new API layer with retry logic and circuit breaker', author: 'Sarah Kim' },
      { ticketId: 'KWP-870', description: 'Updated all dependencies for Q1 security audit compliance', author: 'Bob Chen' }
    ],
    bugfixes: [
      { ticketId: 'KWP-891', description: 'Fixed authentication token refresh race condition causing 401 errors during concurrent API calls', author: 'Bob Chen' },
      { ticketId: 'KWP-898', description: 'Resolved memory leak in dashboard when switching between date ranges rapidly', author: 'Alice Park' }
    ],
    security: [
      { ticketId: 'KWP-870', description: 'Patched 3 CVEs in transitive dependencies (axios, lodash, minimist)', author: 'Bob Chen' }
    ]
  },
  migrationNotes: 'Database schema update required: Run `migration-v4.2.0.sql` before deployment. Backward compatible — no downtime needed. New `notification_preferences` table added with default values for all existing users.'
};

function ReleaseNotes() {
  const dispatch = useDispatch();
  const { releaseId, workspaceId } = useParams();

  const [notes, setNotes] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');
  const [isOpenEmail, setOpenEmail] = useState(false);
  const [emailForm] = KoForm.useForm();

  const releaseBasePath = workspaceId
    ? `/workspace/${workspaceId}/development/release`
    : '/release';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Releases', path: releaseBasePath },
      { title: 'Release Notes' }
    ]));
    setNotes(MOCK_NOTES);
  }, [workspaceId]);

  const handleRegenerate = () => {
    setNotes(MOCK_NOTES);
    KoToast.success('Release notes regenerated from merged PRs!');
  };

  const handleSendEmail = () => {
    setOpenEmail(false);
    emailForm.resetFields();
    KoToast.success('Release notes email sent successfully!');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => KoToast.success('Copied to clipboard!'),
      () => KoToast.error('Copy failed.')
    );
  };

  const tabItems = [
    { key: 'preview', label: 'Preview' },
    { key: 'email', label: 'Email Draft' },
    { key: 'embed', label: 'Embed / API' }
  ];

  const embedSnippet = `<!-- Changelog Widget -->
<script
  src="https://cdn.kwapio.com/widget.js"
  data-project="${releaseId || 'YOUR_PROJECT_ID'}"
  data-style="inline"
  data-theme="light"
></script>`;

  const apiSnippet = `# Get all releases
GET /v1/releases
Authorization: Bearer YOUR_API_TOKEN

# Get specific release notes
GET /v1/releases/${notes?.version || 'v4.2.0'}/notes`;

  return (
    <div className='tableCard'>
      <ReleaseSubNav activeKey='release-notes' />

      <KoToolBar
        title='Release Notes'
        search={{ visible: false }}
        filter={{ visible: false, options: [] }}
        buttonList={[
          { label: 'Regenerate', icon: <KoIcon color='#565555' name='ReloadOutlined' width={16} height={16} />, onClick: handleRegenerate },
          { label: 'Send Email', icon: <KoIcon color='#565555' name='MailOutlined' width={16} height={16} />, onClick: () => setOpenEmail(true) }
        ]}
      />

      <div className={styles.flowBanner}>
        <KoIcon name='FileTextOutlined' width={14} height={14} />
        <KoText text='Step 5: Release Notes — Document changes and communicate to stakeholders' />
      </div>

      {!notes && (
        <div className={styles.emptyState}>
          <KoEmpty description='No release notes yet' />
          <KoButton type='primary' label='Generate Release Notes' onClick={handleRegenerate} />
        </div>
      )}

      {notes && (
        <>
          <div className={styles.tabsWrap}>
            <KoTabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
          </div>

          {activeTab === 'preview' && (
            <div className={styles.previewWrap}>
              <div className={styles.previewBar}>
                <div className={styles.previewDot} style={{ background: '#ef4444' }} />
                <div className={styles.previewDot} style={{ background: '#f59e0b' }} />
                <div className={styles.previewDot} style={{ background: '#22c55e' }} />
                <KoText className={styles.previewPath} text={`release-notes/${notes?.version}.md`} />
              </div>

              <div className={styles.previewContent}>
                <div className={styles.previewTitle}>{notes?.name || 'Release'}</div>
                <div className={styles.previewVersion}>
                  {notes?.version} · {notes?.date} · {notes?.prCount} merged PRs · {notes?.ticketCount} tickets
                </div>

                {Object.entries(SECTION_CONFIG).map(([key, cfg]) => {
                  const items = notes?.sections?.[key] || [];
                  if (!items.length) return null;
                  return (
                    <div key={key} className={styles.noteSection}>
                      <div className={styles.noteSectionTitle} style={{ color: cfg.color }}>
                        <KoIcon name={cfg.icon} width={14} height={14} color={cfg.color} />
                        {cfg.label}
                      </div>
                      {items.map((item, i) => (
                        <div key={i} className={styles.noteItem}>
                          <span className={styles.noteTicket}>{item.ticketId}</span>
                          <KoText className={styles.noteDesc} text={item.description} />
                          <KoText className={styles.noteAuthor} text={item.author} />
                        </div>
                      ))}
                    </div>
                  );
                })}

                {notes?.migrationNotes && (
                  <div className={styles.migrationBox}>
                    <div className={styles.migrationTitle}>
                      <KoIcon name='WarningOutlined' width={13} height={13} />
                      Migration Notes
                    </div>
                    <KoText text={notes.migrationNotes} />
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div className={styles.emailDraftWrap}>
              <div className={styles.emailCard}>
                <div className={styles.emailField}>
                  <span className={styles.emailFieldLabel}>To</span>
                  <KoSelect defaultValue='all' style={{ flex: 1 }}
                    options={[
                      { label: 'All Customers', value: 'all' },
                      { label: 'Enterprise Only', value: 'enterprise' },
                      { label: 'Pro Plan', value: 'pro' }
                    ]} />
                </div>
                <div className={styles.emailField}>
                  <span className={styles.emailFieldLabel}>From</span>
                  <KoInput defaultValue='product@yourapp.com' bordered={false} />
                </div>
                <div className={styles.emailField}>
                  <span className={styles.emailFieldLabel}>Subject</span>
                  <KoInput defaultValue={`What's new in ${notes?.version} — ${notes?.name}`} bordered={false} />
                </div>
                <div className={styles.emailBody} contentEditable suppressContentEditableWarning>
                  <p>Hi {'{{first_name}}'},</p>
                  <p>We have just released <strong>{notes?.version} — {notes?.name}</strong>.</p>
                  <p><strong>What is new:</strong></p>
                  <ul>
                    {(notes?.sections?.features || []).map((f, i) => (<li key={i}>{f.description}</li>))}
                    {(notes?.sections?.bugfixes || []).map((f, i) => (<li key={i}>{f.description}</li>))}
                  </ul>
                  <p><a href='#'>Read the full release notes</a></p>
                  <p>The Product Team</p>
                </div>
                <div className={styles.emailActions}>
                  <KoButton type='primary' label='Send Now' onClick={() => setOpenEmail(true)} />
                  <KoButton label='Schedule for Release' onClick={() => KoToast.success('Scheduled for release day')} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'embed' && (
            <div className={styles.embedWrap}>
              <div className={styles.embedCard}>
                <KoTitle level={5} text='Embed on Customer Website' />
                <KoTag color='success'>Live — Auto-updates on release</KoTag>
                <KoText className={styles.embedDesc}
                  text='Drop this snippet into your website — the changelog updates automatically each time you ship a release.' />
                <div className={styles.codeBlock}>
                  <pre>{embedSnippet}</pre>
                  <KoButton className={styles.copyBtn} label='Copy' onClick={() => copyToClipboard(embedSnippet)} />
                </div>
                <KoTitle level={5} text='REST API' style={{ marginTop: 20 }} />
                <div className={styles.codeBlock}>
                  <pre>{apiSnippet}</pre>
                  <KoButton className={styles.copyBtn} label='Copy' onClick={() => copyToClipboard(apiSnippet)} />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <KoModal title='Send Release Email' centered open={isOpenEmail}
        handleOk={() => setOpenEmail(false)} onClose={() => setOpenEmail(false)}
        width={'min(520px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => setOpenEmail(false)} />,
          <KoButton key='send' type='primary' label='Send' onClick={() => emailForm.submit()} />
        ]}
      >
        <EmailSendCrud form={emailForm} handleFinish={handleSendEmail} />
      </KoModal>
    </div>
  );
}

function EmailSendCrud({ form, handleFinish }) {
  const AudienceWidget = ({ value, onChange }) => (
    <KoSelect value={value} onChange={onChange} placeholder='Select recipients'
      options={[
        { label: 'All Customers', value: 'all' },
        { label: 'Enterprise Only', value: 'enterprise' },
        { label: 'Pro Plan', value: 'pro' }
      ]} />
  );
  const SendTimeWidget = ({ value, onChange }) => (
    <KoSelect value={value} onChange={onChange}
      options={[
        { label: 'Immediately on release', value: 'immediate' },
        { label: 'Manual', value: 'manual' }
      ]} />
  );

  const meta = {
    columns: 4, formItemLayout: null, colon: true,
    fields: [
      { key: 'audience', label: 'Recipients', colSpan: 4, widget: AudienceWidget, forwardRef: true },
      { key: 'sendTime', label: 'Send Time', colSpan: 4, widget: SendTimeWidget, forwardRef: true },
      { key: 'cc', label: 'CC Internal Team', colSpan: 4, widget: KoInput, widgetProps: { placeholder: 'team@yourapp.com' } }
    ]
  };

  return (
    <KoForm form={form} layout='vertical' onFinish={handleFinish}>
      <KoFormBuilder form={form} meta={meta} />
    </KoForm>
  );
}

export default ReleaseNotes;
